import type { GetAuthUsersResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetUsersQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetUsersArgs = Record<string, never>;

/**
 * Flat user list backing the legacy `getUsers` GraphQL query.
 *
 * Reuses the already-migrated `frontend.user.users` tRPC procedure (the same
 * source as `useGetAuthUsers`) but projects the richer
 * `GetAuthUsersResponseRow` down to the narrow `getUsers` shape and sorts by
 * `FriendlyName` ascending — the data-layer repository does not order, so the
 * `order_by: { FriendlyName: asc }` from the GraphQL document is reproduced
 * here. The server already filters out `ThirdPartyRespondent` rows, so no
 * client-side filtering is required.
 */
const mapTrpcUsersToGraphQL = (
  trpcData: GetAuthUsersResponseRow[]
): GetUsersQuery => ({
  user: [...trpcData]
    .sort((a, b) => (a.FriendlyName ?? '').localeCompare(b.FriendlyName ?? ''))
    .map((u) => ({
      Id: u.Id,
      FriendlyName: u.FriendlyName,
      Status: u.Status,
      RoleKey: u.RoleKey,
      Email: u.Email,
      Department: u.Department,
      JobTitle: u.JobTitle,
      OfficeLocation: u.OfficeLocation,
      LastSeen: u.LastSeen,
      IsCustomerSupport: u.IsCustomerSupport,
    })),
});

export const useGetUsers = createQueryHook<
  UseGetUsersArgs,
  GetAuthUsersResponseRow[],
  GetUsersQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.user.users.queryOptions(),
  mapTrpcDataToGraphQL: mapTrpcUsersToGraphQL,
  graphqlDocument: GetUsersDocument,
  // The user list is shared by ~8 field-config hooks that often render in the
  // same form. Use cache-first (Apollo's default, as the pre-migration
  // `useQuery(GetUsersDocument)` consumers had) so concurrent identical queries
  // dedupe into one request instead of the factory-default `no-cache`, which
  // fires one request per consumer.
  graphqlFetchPolicy: 'cache-first',
});
